import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Vote, 
  ArrowLeft, 
  Loader2, 
  Plus, 
  Trash2, 
  Play,
  ExternalLink,
  CreditCard,
  Image,
  Video,
  StopCircle,
  BarChart3,
  Upload,
  Gift,
  Bell,
  Percent,
  Ticket
} from "lucide-react";
import { toast } from "sonner";
import { pollsAPI, paymentsAPI } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PaymentModal from "../components/PaymentModal";

export default function EditPoll() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [closing, setClosing] = useState(false);
  const [addingOption, setAddingOption] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const [newOption, setNewOption] = useState({ title: "", description: "", image_url: "", video_url: "" });

  useEffect(() => {
    loadPoll();
  }, [id]);

  const loadPoll = async () => {
    try {
      const res = await pollsAPI.get(id);
      setPoll(res.data);
    } catch (error) {
      toast.error("Sondage non trouvé");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setNewOption({...newOption, image_url: res.data.url});
      toast.success("Image uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  };

  const handleAddOption = async () => {
    if (!newOption.title.trim()) {
      toast.error("Titre requis");
      return;
    }
    setAddingOption(true);
    try {
      await pollsAPI.addOption(id, newOption);
      toast.success("Option ajoutée");
      setNewOption({ title: "", description: "", image_url: "", video_url: "" });
      loadPoll();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAddingOption(false);
    }
  };

  const handleRemoveOption = async (optionId) => {
    try {
      await pollsAPI.removeOption(id, optionId);
      toast.success("Option supprimée");
      loadPoll();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await pollsAPI.activate(id);
      toast.success("Sondage activé !");
      await refreshUser();
      loadPoll();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("Paiement requis pour activer le sondage");
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors de l'activation");
      }
    } finally {
      setActivating(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm("Fermer le sondage ? Les votes seront figés.")) return;
    setClosing(true);
    try {
      await pollsAPI.close(id);
      toast.success("Sondage fermé");
      loadPoll();
    } catch (error) {
      toast.error("Erreur lors de la fermeture");
    } finally {
      setClosing(false);
    }
  };

  const handlePayment = async () => {
    try {
      const res = await paymentsAPI.createCheckout({
        item_type: "poll",
        item_id: id
      });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error("Erreur lors du paiement");
    }
  };

  const canActivate = poll?.options?.length >= 2;
  const needsPayment = !poll?.is_paid && user?.free_poll_used;
  const isActive = poll?.status === "active";
  const isClosed = poll?.status === "closed";
  const isDraft = poll?.status === "draft";

  const totalVotes = poll?.total_votes || 0;
  const getVotePercentage = (votes) => totalVotes > 0 ? ((votes / totalVotes) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>{poll?.title}</h1>
              <p className="text-sm text-muted-foreground">{poll?.organizer_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isClosed ? (
              <Badge className="bg-slate-100 text-slate-700">Fermé</Badge>
            ) : isActive ? (
              <Badge className="bg-green-100 text-green-700">Actif</Badge>
            ) : poll?.is_paid ? (
              <Badge className="bg-blue-100 text-blue-700">Payé</Badge>
            ) : !user?.free_poll_used ? (
              <Badge className="bg-amber-100 text-amber-700">Gratuit</Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-700">Non payé</Badge>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full"
              onClick={() => window.open(`/poll/${id}/vote`, '_blank')}
              data-testid="public-vote-link"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Lien vote
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full"
              onClick={() => window.open(`/poll/${id}/results`, '_blank')}
              data-testid="public-results-link"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Lien résultats
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="options" className="space-y-6">
          <TabsList className="bg-card">
            <TabsTrigger value="options">
              Options ({poll?.options?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="rewards">
              <Gift className="w-4 h-4 mr-2" />
              Récompenses
            </TabsTrigger>
            <TabsTrigger value="results">
              <BarChart3 className="w-4 h-4 mr-2" />
              Résultats ({totalVotes} votes)
            </TabsTrigger>
          </TabsList>

          {/* Options Tab */}
          <TabsContent value="options">
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Vote className="w-5 h-5" />
                  Options de vote
                </CardTitle>
                <CardDescription>
                  Ajoutez les options (candidats, logos, propositions...) que les participants pourront choisir
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isDraft && (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-xl">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        placeholder="Titre de l'option *"
                        value={newOption.title}
                        onChange={(e) => setNewOption({...newOption, title: e.target.value})}
                        className="h-10 bg-white"
                        data-testid="option-title-input"
                      />
                      <Input
                        placeholder="Description (optionnel)"
                        value={newOption.description}
                        onChange={(e) => setNewOption({...newOption, description: e.target.value})}
                        className="h-10 bg-white"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Image Upload */}
                      <div className="space-y-2">
                        <input
                          type="file"
                          ref={imageInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                        {newOption.image_url ? (
                          <div className="relative group">
                            <img 
                              src={newOption.image_url} 
                              alt="Option" 
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => setNewOption({...newOption, image_url: ""})}
                              className="absolute top-1 right-1 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full h-10"
                            onClick={() => imageInputRef.current?.click()}
                            disabled={uploadingImage}
                          >
                            {uploadingImage ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Ajouter une image
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      <div className="relative">
                        <Video className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                        <Input
                          placeholder="URL vidéo YouTube (optionnel)"
                          value={newOption.video_url}
                          onChange={(e) => setNewOption({...newOption, video_url: e.target.value})}
                          className="h-10 bg-white pl-10"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleAddOption}
                      disabled={addingOption}
                      className="rounded-full bg-primary hover:bg-primary/90"
                      data-testid="add-option-btn"
                    >
                      {addingOption ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Ajouter l'option
                    </Button>
                  </div>
                )}

                {/* Options List */}
                <div className="grid md:grid-cols-2 gap-4">
                  {poll?.options?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 md:col-span-2">
                      Aucune option définie. Ajoutez au moins 2 options.
                    </p>
                  ) : (
                    poll?.options?.map((option) => (
                      <Card key={option.id} className="bg-muted/30 border-none group" data-testid={`option-${option.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground">{option.title}</h4>
                              {option.description && (
                                <p className="text-sm text-muted-foreground">{option.description}</p>
                              )}
                            </div>
                            {isDraft && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                                onClick={() => handleRemoveOption(option.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          
                          {option.image_url && (
                            <img 
                              src={option.image_url} 
                              alt={option.title}
                              className="w-full h-32 object-cover rounded-lg mb-3"
                            />
                          )}
                          
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{option.votes || 0} votes</span>
                            <span className="font-medium text-primary">{getVotePercentage(option.votes || 0)}%</span>
                          </div>
                          <Progress value={getVotePercentage(option.votes || 0)} className="h-2 mt-2" />
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Action Card */}
            {!isClosed && (
              <Card className="bg-card mt-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground mb-1" style={{ fontFamily: 'Outfit' }}>
                        {isDraft ? "Activer le sondage" : "Fermer le sondage"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {isDraft 
                          ? (!canActivate 
                              ? "Ajoutez au moins 2 options pour activer"
                              : needsPayment 
                              ? "Payez 2€ TTC pour activer le sondage"
                              : "Les participants pourront voter")
                          : "Figer les votes et terminer le sondage"
                        }
                      </p>
                    </div>
                    <div className="flex gap-3">
                      {isDraft && needsPayment && canActivate && (
                        <Button
                          onClick={() => setShowPaymentModal(true)}
                          variant="outline"
                          className="rounded-full border-blue-500 text-blue-600 hover:bg-blue-50"
                          data-testid="pay-poll-btn"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Payer 2€ TTC
                        </Button>
                      )}
                      {isDraft ? (
                        <Button
                          onClick={handleActivate}
                          disabled={!canActivate || activating || needsPayment}
                          className="rounded-full bg-green-600 hover:bg-green-700"
                          data-testid="activate-poll-btn"
                        >
                          {activating ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Activer
                        </Button>
                      ) : (
                        <Button
                          onClick={handleClose}
                          disabled={closing}
                          variant="outline"
                          className="rounded-full"
                        >
                          {closing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <StopCircle className="w-4 h-4 mr-2" />
                          )}
                          Fermer le sondage
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <div className="grid gap-6">
              {/* Reward Settings */}
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Gift className="w-5 h-5 text-amber-500" />
                    Récompense pour les participants
                  </CardTitle>
                  <CardDescription>
                    Offrez un code promo ou une chance de gagner un lot aux participants
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Ticket className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="font-medium">Activer les récompenses</p>
                        <p className="text-sm text-muted-foreground">
                          Les participants reçoivent une récompense après leur vote
                        </p>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={poll?.reward_enabled || false}
                        onChange={async (e) => {
                          try {
                            await pollsAPI.update(id, { reward_enabled: e.target.checked });
                            setPoll(prev => ({ ...prev, reward_enabled: e.target.checked }));
                          } catch (error) {
                            toast.error("Erreur de sauvegarde");
                          }
                        }}
                        className="rounded border-input w-5 h-5"
                      />
                    </label>
                  </div>

                  {poll?.reward_enabled && (
                    <div className="space-y-4 p-4 border rounded-xl bg-amber-50 dark:bg-amber-950/20">
                      {/* Reward Type */}
                      <div className="space-y-2">
                        <Label>Type de récompense</Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={poll?.reward_type !== "draw" ? "default" : "outline"}
                            size="sm"
                            className="flex-1"
                            onClick={async () => {
                              try {
                                await pollsAPI.update(id, { reward_type: "code" });
                                setPoll(prev => ({ ...prev, reward_type: "code" }));
                              } catch (error) {}
                            }}
                          >
                            <Ticket className="w-4 h-4 mr-2" />
                            Code promo (100%)
                          </Button>
                          <Button
                            type="button"
                            variant={poll?.reward_type === "draw" ? "default" : "outline"}
                            size="sm"
                            className="flex-1"
                            onClick={async () => {
                              try {
                                await pollsAPI.update(id, { reward_type: "draw" });
                                setPoll(prev => ({ ...prev, reward_type: "draw" }));
                              } catch (error) {}
                            }}
                          >
                            <Percent className="w-4 h-4 mr-2" />
                            Tirage au sort
                          </Button>
                        </div>
                      </div>

                      {/* Draw Chance (if type is draw) */}
                      {poll?.reward_type === "draw" && (
                        <div className="space-y-2">
                          <Label>Chance de gagner (%)</Label>
                          <div className="flex items-center gap-4">
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              value={poll?.reward_draw_chance || 10}
                              onChange={async (e) => {
                                const val = Math.min(100, Math.max(1, parseInt(e.target.value) || 10));
                                try {
                                  await pollsAPI.update(id, { reward_draw_chance: val });
                                  setPoll(prev => ({ ...prev, reward_draw_chance: val }));
                                } catch (error) {}
                              }}
                              className="w-24"
                            />
                            <span className="text-muted-foreground">% des participants gagnent</span>
                          </div>
                        </div>
                      )}

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom de la récompense</Label>
                          <Input
                            placeholder="Ex: -15% sur votre prochaine commande"
                            value={poll?.reward_code_name || ""}
                            onChange={async (e) => {
                              try {
                                await pollsAPI.update(id, { reward_code_name: e.target.value });
                                setPoll(prev => ({ ...prev, reward_code_name: e.target.value }));
                              } catch (error) {}
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Code promo</Label>
                          <Input
                            placeholder="Ex: SONDAGE15"
                            value={poll?.reward_code || ""}
                            onChange={async (e) => {
                              try {
                                await pollsAPI.update(id, { reward_code: e.target.value });
                                setPoll(prev => ({ ...prev, reward_code: e.target.value }));
                              } catch (error) {}
                            }}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Validité</Label>
                        <Input
                          placeholder="Ex: Valable jusqu'au 31/12/2026 - Non cumulable"
                          value={poll?.reward_code_validity || ""}
                          onChange={async (e) => {
                            try {
                              await pollsAPI.update(id, { reward_code_validity: e.target.value });
                              setPoll(prev => ({ ...prev, reward_code_validity: e.target.value }));
                            } catch (error) {}
                          }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Opt-in Settings */}
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Bell className="w-5 h-5 text-blue-500" />
                    Inscription à la newsletter
                  </CardTitle>
                  <CardDescription>
                    Proposez aux participants de s'inscrire à votre liste de diffusion
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Activer l'opt-in newsletter</p>
                        <p className="text-sm text-muted-foreground">
                          En échange de la récompense, le participant peut s'inscrire à votre liste
                        </p>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={poll?.optin_enabled || false}
                        onChange={async (e) => {
                          try {
                            await pollsAPI.update(id, { optin_enabled: e.target.checked });
                            setPoll(prev => ({ ...prev, optin_enabled: e.target.checked }));
                          } catch (error) {
                            toast.error("Erreur de sauvegarde");
                          }
                        }}
                        className="rounded border-input w-5 h-5"
                      />
                    </label>
                  </div>

                  {poll?.optin_enabled && (
                    <div className="space-y-2 p-4 border rounded-xl">
                      <Label>Message opt-in personnalisé</Label>
                      <Input
                        placeholder="J'accepte de recevoir les actualités et offres de l'organisateur"
                        value={poll?.optin_message || ""}
                        onChange={async (e) => {
                          try {
                            await pollsAPI.update(id, { optin_message: e.target.value });
                            setPoll(prev => ({ ...prev, optin_message: e.target.value }));
                          } catch (error) {}
                        }}
                      />
                      
                      {/* Show opt-in count */}
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          <strong>{poll?.optin_list?.length || 0}</strong> inscrit(s) à votre newsletter
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <BarChart3 className="w-5 h-5" />
                  Résultats du sondage
                </CardTitle>
                <CardDescription>
                  {totalVotes} vote{totalVotes > 1 ? 's' : ''} au total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {poll?.options
                    ?.sort((a, b) => (b.votes || 0) - (a.votes || 0))
                    .map((option, index) => (
                      <div key={option.id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {index === 0 && totalVotes > 0 && (
                              <Badge className="bg-amber-100 text-amber-700">1er</Badge>
                            )}
                            <span className="font-medium text-foreground">{option.title}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-semibold text-foreground">{option.votes || 0}</span>
                            <span className="text-muted-foreground ml-2">({getVotePercentage(option.votes || 0)}%)</span>
                          </div>
                        </div>
                        <Progress 
                          value={getVotePercentage(option.votes || 0)} 
                          className={`h-3 ${index === 0 && totalVotes > 0 ? '[&>div]:bg-amber-500' : ''}`}
                        />
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Payment Modal */}
      <PaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        onPay={handlePayment}
        itemType="poll"
        itemTitle={poll?.title}
        price={2.00}
        isFreeAvailable={user?.free_poll_used}
      />
    </div>
  );
}
